// @ts-ignore
import {ClassName, States} from '@dfb/components/dropdown/constants';
import {CSSObject} from '@emotion/serialize';
import {useEffect, useState} from 'react';
import Select, {components, StylesConfig, ValueType} from 'react-select';
import {Placeholder} from 'react-select/animated';
import {ContainerProps} from 'react-select/src/components/containers';
import Control from 'react-select/src/components/Control';
import {DropdownIndicator, IndicatorSeparator} from 'react-select/src/components/indicators';
import {MenuList} from 'react-select/src/components/Menu';
import Option from 'react-select/src/components/Option';
import SingleValue from 'react-select/src/components/SingleValue';
import {Option as OptionInterface} from 'react-select/src/filters';
import {ActionMeta, OptionTypeBase} from 'react-select/src/types';
import {DropDownSelectHandler} from '../../types/Handler';
import {DfbDropdowmDownSymbol} from '../symbol/Symbol';
import DropdownOption from './DropdownOption';

export type DropdownOptionType<ValueTypes = number | string, ObjectType extends object = any> = {
    value: ValueTypes
    label: string
    object?: ObjectType
}

export type DropdownProps<ValueTypes = number | string, ObjectType extends object = any> = {
    id?: string
    label: string
    options: DropdownOptionType<ValueTypes, ObjectType>[]
    value?: ValueTypes
    errorMessage?: string
    required?: boolean
    onChange?: DropDownSelectHandler<ValueTypes, ObjectType>
    placeholder?: string
    customOption?: typeof Option
    customControl?: typeof Control
    filterOption?: ((option: OptionInterface, rawInput: string) => boolean) | null
    customSingleValue?: typeof SingleValue
    className?: string
    disabled?: boolean
    tabIndex?: string
};

const CustomMenuList: typeof MenuList = props => {
    return (
        <components.MenuList {...props} className={ClassName.LISTBOX}>
            <ul>
                {props.children}
            </ul>
        </components.MenuList>
    );
};

const CustomIndicatorSeparator: typeof IndicatorSeparator = (props) => {
    return (<></>);
};

const CustomDropdownIndicator: typeof DropdownIndicator = props => {
    return (
        <components.DropdownIndicator {...props} className={`${ClassName.TOGGLE}`}>
            <DfbDropdowmDownSymbol/>
        </components.DropdownIndicator>
    );
};

const CustomSelectContainer = (props: ContainerProps<any, any>, errorMessage?: string) => {
    return (
        <div
            // workaround for bug in dfb-ui
            data-default-label=""
            className={`dfb-Dropdown${props.selectProps.menuIsOpen ? ` ${States.OPEN}` : ''}${!errorMessage ? '' : ` ${States.INVALID}`}${!props.isDisabled ? '' : ` is-disabled`}`}>
            <components.SelectContainer {...props}>
                {props.children}
            </components.SelectContainer>
            {
                errorMessage &&
                <p className="dfb-Paragraph dfb-Paragraph--small dfb-Dropdown-errorMessage">
                    {errorMessage}
                </p>
            }
        </div>
    );
};

const CustomValueContainer = (props, label: string) => {
    return (
        <components.ValueContainer  {...props}>
            <div className={ClassName.SELECT}>
                <div className={ClassName.COMBOBOX}>
                    <div className={ClassName.LABEL}>{label}</div>
                    {props.children}
                </div>
            </div>
        </components.ValueContainer>
    );
};

const CustomPlaceholder: typeof Placeholder = props => (
    <components.Placeholder
        className={ClassName.VALUE}
        {...props}
        children={props.children}
    />
);

const CustomControl: typeof Control = props => (
    <components.Control
        {...props}
        children={props.children}
    />
);

const CustomSingleValue: typeof SingleValue = ({children, ...props}) => (
    <components.SingleValue {...props}>{children}</components.SingleValue>
);

const Dropdown = <ValueTypes, ObjectType extends object>(props: DropdownProps<ValueTypes, ObjectType>) => {
    const [value, setValue] = useState<DropdownOptionType<ValueTypes, ObjectType> | undefined>(props.value ? props.options.find(x => x.value === props.value) : undefined);

    useEffect(() => {
        if (props.value) {
            setValue(props.options.find(x => x.value === props.value));
        } else {
            setValue(null);
        }
    }, [props]);

    const removeCssRules = (base: CSSObject, properties: string[]): CSSObject => {
        const newCss = {...base};
        properties.forEach(property => {
            delete newCss[property];
        });
        return newCss;
    };

    const styles: StylesConfig<OptionTypeBase, boolean> = {
        valueContainer: (base: CSSObject) => removeCssRules(base, ['padding']),
        indicatorsContainer: (base: CSSObject) => removeCssRules(base, ['padding', 'color']),
        dropdownIndicator: (base: CSSObject) => removeCssRules(base, ['padding', 'color', 'transition']),
        menu: (base: CSSObject) => {
            const newCss = removeCssRules(base, ['marginTop', 'marginBottom']);
            return {
                ...newCss,
                zIndex: 50
            };
        },
        control: (base: CSSObject) => {
            let newCss = removeCssRules(base, ['backgroundColor', 'borderColor', 'boxShadow', 'minHeight', 'position', 'cursor']);
            return {
                ...newCss,
                border: 'none',
                lineHeight: 1,
                width: '100%'
            };
        },
        option: (base: CSSObject) => removeCssRules(base, ['padding', 'background']),
        placeholder: (base: CSSObject) => removeCssRules(base, ['color', 'marginLeft', 'marginRight', 'top', 'transform']),
        singleValue: (base: CSSObject) => removeCssRules(base, ['color', 'marginLeft', 'marginRight', 'top', 'transform']),
        input: (base: CSSObject) => removeCssRules(base, ['margin'])
    };

    const handleChange = (value: ValueType<DropdownOptionType<ValueTypes, ObjectType>, boolean>, actionMeta: ActionMeta<OptionTypeBase>) => {
        const selectedOption = value as DropdownOptionType<ValueTypes, ObjectType>;
        setValue(selectedOption);

        if (props.onChange) {
            props.onChange(selectedOption.value, selectedOption.label, selectedOption.object);
        }
    };

    return <Select
        tabIndex={props.tabIndex}
        value={value}
        onChange={handleChange}
        options={props.options}
        placeholder={props.placeholder ?? ''}
        components={{
            Option: props.customOption ? props.customOption : DropdownOption,
            MenuList: CustomMenuList,
            DropdownIndicator: CustomDropdownIndicator,
            IndicatorSeparator: CustomIndicatorSeparator,
            SelectContainer: (selectContainerProps) => CustomSelectContainer(selectContainerProps, props.errorMessage),
            Placeholder: CustomPlaceholder,
            ValueContainer: valueProps => CustomValueContainer(valueProps, props.label),
            Control: props.customControl ? props.customControl : CustomControl,
            SingleValue: props.customSingleValue ? props.customSingleValue : CustomSingleValue
        }}
        styles={styles}
        filterOption={props.filterOption}
        className={props.className}
        isDisabled={props.disabled}
    />;
};

export default Dropdown;
